import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, CheckCircle, Star, Users, Building2, Cpu } from "lucide-react";
import PriorityListModal from "../components/shared/PriorityListModal";
import CTABand from "../components/shared/CTABand";

function PageHero({ overline, title, subtitle }) {
  return (
    <section className="page-hero" style={{ backgroundColor: "var(--surface)" }} data-testid="attend-hero">
      <div className="container-xl">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="overline-label mb-4">{overline}</div>
          <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-white mb-6 max-w-3xl"
            style={{ fontFamily: "var(--font-heading)" }}>{title}</h1>
          <p className="text-lg max-w-2xl" style={{ color: "var(--text-muted)" }}>{subtitle}</p>
        </motion.div>
      </div>
    </section>
  );
}

function RevealSection({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 32 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

const TICKETS = [
  {
    id: "smb",
    icon: Building2,
    label: "Business & SMB",
    earlyBird: "$399",
    regular: "$499",
    late: "$599",
    currency: "CAD",
    desc: "For business owners, managers, and professionals adopting AI without a technical background.",
    perks: [
      "Full 2-day access to all sessions",
      "SMB-focused breakout tracks",
      "Networking reception access",
      "Expo floor & exhibitor access",
      "Event app & digital badge",
    ],
    featured: false,
    color: "#00D4FF",
  },
  {
    id: "enterprise",
    icon: Users,
    label: "Enterprise & Leadership",
    earlyBird: "$699",
    regular: "$899",
    late: "$1,099",
    currency: "CAD",
    desc: "For C-suite, VPs, and directors leading AI transformation across their organizations.",
    perks: [
      "Full 2-day access to all sessions",
      "Executive breakout & roundtables",
      "AI-curated 1:1 meeting access",
      "Priority keynote seating",
      "Networking reception + leadership dinner",
      "Expo floor & exhibitor access",
    ],
    featured: true,
    color: "#7B2FBE",
  },
  {
    id: "technical",
    icon: Cpu,
    label: "Technical & Builders",
    earlyBird: "$449",
    regular: "$599",
    late: "$749",
    currency: "CAD",
    desc: "For developers, data scientists, and ML engineers building and deploying production AI.",
    perks: [
      "Full 2-day access to all sessions",
      "Hands-on workshops & labs",
      "Technical deep-dive tracks",
      "Startup & Demo Zone access",
      "Developer networking event",
    ],
    featured: false,
    color: "#00D4FF",
  },
];

export default function AttendPage() {
  const [showModal, setShowModal] = useState(false);

  return (
    <div data-testid="attend-page">
      <PageHero
        overline="Join Core AI Summit 2027"
        title="This is where your AI journey accelerates."
        subtitle="Two days of practical AI implementation, curated connections, and hands-on learning. Built for the business leader, the enterprise transformer, and the technical builder."
      />

      {/* Why Attend */}
      <section className="section-py">
        <div className="container-xl">
          <RevealSection>
            <div className="overline-label mb-4">Why Attend</div>
            <h2 className="text-4xl font-black tracking-tight text-white mb-12"
              style={{ fontFamily: "var(--font-heading)" }}>What you'll walk away with.</h2>
          </RevealSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Proven playbooks", desc: "Real-world implementation stories from companies actively using AI. No theory, no hype." },
              { title: "Curated connections", desc: "AI-matched 1:1 meetings with decision-makers and partners in your exact market." },
              { title: "Hands-on skills", desc: "Workshops and labs that build muscle memory — not just slide decks." },
              { title: "A 90-day roadmap", desc: "Walk away with clear, prioritized next steps for your business's AI journey." },
              { title: "Vendor clarity", desc: "60+ vetted exhibitors with live demos. Find the tools that actually fit your context." },
              { title: "A community", desc: "Join Canada's emerging AI community — before it becomes the obvious place to be." },
            ].map((item, i) => (
              <RevealSection key={item.title} delay={i * 0.07}>
                <div className="card card-glow p-6" data-testid={`why-attend-card-${i}`}>
                  <CheckCircle size={18} className="mb-3" style={{ color: "var(--cyan)" }} />
                  <h4 className="font-bold text-white mb-2" style={{ fontFamily: "var(--font-heading)" }}>{item.title}</h4>
                  <p className="text-sm" style={{ color: "var(--text-muted)" }}>{item.desc}</p>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* Ticket Tiers */}
      <section id="tickets" className="section-py" style={{ backgroundColor: "var(--surface)" }} data-testid="tickets-section">
        <div className="container-xl">
          <RevealSection>
            <div className="text-center max-w-2xl mx-auto mb-4">
              <div className="overline-label mb-4">Ticket Pricing</div>
              <h2 className="text-4xl font-black tracking-tight text-white mb-4"
                style={{ fontFamily: "var(--font-heading)" }}>Choose your pathway.</h2>
              <p className="text-base" style={{ color: "var(--text-muted)" }}>
                All tickets provide full access to all sessions. Differentiation is based on your role, not your access.
              </p>
              <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full text-xs"
                style={{ background: "rgba(0,212,255,0.08)", border: "1px solid rgba(0,212,255,0.2)", color: "var(--cyan)", fontFamily: "var(--font-mono)" }}>
                <Star size={12} fill="currentColor" /> Early bird pricing available on priority list
              </div>
            </div>
          </RevealSection>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-12">
            {TICKETS.map((ticket, i) => {
              const Icon = ticket.icon;
              return (
                <RevealSection key={ticket.id} delay={i * 0.1}>
                  <div className={`ticket-card ${ticket.featured ? "featured" : ""}`} data-testid={`ticket-card-${ticket.id}`}>
                    {ticket.featured && (
                      <div className="absolute top-0 right-0 left-0 h-0.5 rounded-t-2xl"
                        style={{ background: "linear-gradient(90deg, var(--cyan), var(--violet))" }} />
                    )}
                    {ticket.featured && (
                      <div className="overline-label mb-4 text-center">Most Popular</div>
                    )}
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ background: `${ticket.color}18`, border: `1px solid ${ticket.color}33` }}>
                        <Icon size={18} style={{ color: ticket.color }} />
                      </div>
                      <div className="font-bold text-white" style={{ fontFamily: "var(--font-heading)" }}>{ticket.label}</div>
                    </div>
                    <p className="text-sm mb-6" style={{ color: "var(--text-muted)" }}>{ticket.desc}</p>

                    <div className="mb-6 p-4 rounded-xl" style={{ background: "var(--bg)" }}>
                      <div className="text-xs uppercase tracking-widest mb-3" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Indicative Pricing</div>
                      <div className="space-y-2">
                        {[["Early Bird", ticket.earlyBird], ["Regular", ticket.regular], ["Late/On-site", ticket.late]].map(([label, price]) => (
                          <div key={label} className="flex justify-between text-sm">
                            <span style={{ color: "var(--text-muted)" }}>{label}</span>
                            <span className="font-bold text-white">{price} <span className="text-xs" style={{ color: "var(--text-muted)" }}>{ticket.currency}</span></span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <ul className="space-y-2 mb-8">
                      {ticket.perks.map((perk) => (
                        <li key={perk} className="flex items-start gap-2 text-sm">
                          <CheckCircle size={13} className="flex-shrink-0 mt-0.5" style={{ color: "var(--cyan)" }} />
                          <span style={{ color: "var(--text-muted)" }}>{perk}</span>
                        </li>
                      ))}
                    </ul>

                    <button onClick={() => setShowModal(true)} className={ticket.featured ? "btn-primary w-full justify-center" : "btn-outline w-full justify-center"}
                      data-testid={`ticket-cta-${ticket.id}`}>
                      Join Priority List <ArrowRight size={14} />
                    </button>
                  </div>
                </RevealSection>
              );
            })}
          </div>

          <RevealSection delay={0.3}>
            <div className="text-center mt-8">
              <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                Student discounts (50%) available for Technical track. Group discounts for Enterprise (3+ attendees).
                <br />All prices in Canadian dollars. Tickets not yet on sale — join the priority list for early access.
              </p>
            </div>
          </RevealSection>
        </div>
      </section>

      <CTABand
        title="Secure Early Access to Tickets"
        subtitle="Priority list members get first access to early-bird pricing and speaker announcements."
      />
      <PriorityListModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
}
